import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, History, RotateCw, Loader2 } from "lucide-react";
import { fmtDate } from "../lib/status";

const CATEGORY_TONES = {
  booking:    "bg-blue-100 text-blue-900 border-blue-200",
  damage:     "bg-amber-100 text-amber-900 border-amber-200",
  warning:    "bg-rose-100 text-rose-900 border-rose-200",
  incident:   "bg-orange-100 text-orange-900 border-orange-200",
  inspection: "bg-emerald-100 text-emerald-900 border-emerald-200",
  deposit:    "bg-violet-100 text-violet-900 border-violet-200",
  vehicle:    "bg-slate-100 text-slate-900 border-slate-200",
  staff:      "bg-cyan-100 text-cyan-900 border-cyan-200",
  other:      "bg-slate-100 text-slate-800 border-slate-200",
};

const human = (t) => (t || "").replace(/_/g, " ");

/** Dedicated chronological view of a single booking's activity.
 *
 * Reads from `/api/activity?booking_id=...` so it stays in sync with the
 * global activity feed and is useful for support / disputes / staff handoff.
 */
export default function BookingTimeline() {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: feed }, { data: b }] = await Promise.all([
        api.get("/activity", { params: { booking_id: id, limit: 200 } }),
        api.get(`/bookings/${id}`),
      ]);
      setItems(feed.items || []);
      setBooking(b);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await load();
    })();
    return () => { cancelled = true; };
  }, [load]);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="booking-timeline-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Link
            to={`/app/bookings/${id}`}
            data-testid="booking-timeline-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft className="w-4 h-4" /> Back to booking
          </Link>
          <h1 className="font-display text-3xl font-bold mt-2 flex items-center gap-2">
            <History className="w-6 h-6 text-slate-400" /> Booking timeline
          </h1>
          {booking && (
            <p className="text-sm text-slate-500 mt-1">
              <span className="font-mono">{booking.booking_reference}</span>
              {booking.customer_name ? ` · ${booking.customer_name}` : ""}
            </p>
          )}
        </div>
        <button
          onClick={load}
          data-testid="booking-timeline-refresh-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm"
        >
          <RotateCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white">
        {loading ? (
          <div className="p-10 text-center text-slate-500" data-testid="booking-timeline-loading">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading activity…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="booking-timeline-empty">
            No activity recorded for this booking yet.
          </div>
        ) : (
          <ol className="divide-y divide-slate-100" data-testid="booking-timeline-list">
            {items.map((it) => (
              <li key={it.id} className="px-5 py-4 flex gap-4" data-testid={`booking-timeline-row-${it.action_type}`}>
                <div className="flex flex-col items-center pt-1">
                  <span className={`w-2.5 h-2.5 rounded-full ${(CATEGORY_TONES[it.category] || "").split(" ")[0].replace("bg-", "bg-")}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${CATEGORY_TONES[it.category] || CATEGORY_TONES.other}`}
                    >
                      {it.category}
                    </span>
                    <span className="text-sm font-medium text-slate-900 capitalize">
                      {human(it.action_type)}
                    </span>
                  </div>
                  {it.description && (
                    <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap break-words">
                      {it.description}
                    </p>
                  )}
                  <div className="mt-1 text-xs text-slate-400">
                    {fmtDate(it.created_at)}
                    {it.user_name ? ` · ${it.user_name}` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
